/**
 * Página de generación de reportes CSV del Backoffice.
 * Permite seleccionar tipo de reporte y rango de fechas, y descarga el CSV.
 *
 * @taskId T653
 * @taskId T654
 * @taskId T655
 * @see functional/Cycles/cycle-5/tasks.md HU-29-FE
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Download } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@flexibilitypay/ui';

import { type ReportType } from '../api/reports.api';
import { useExportCsv } from '../hooks/use-export-csv';

const REPORT_TYPES: ReportType[] = ['TRANSACTIONS', 'ACCOUNTS', 'CUSTOMERS'];
const MAX_RANGE_DAYS = 90;
const MS_PER_DAY = 86_400_000;

function getDaysDiff(from: string, to: string): number {
  return Math.round((new Date(to).getTime() - new Date(from).getTime()) / MS_PER_DAY);
}

export function ReportsPage() {
  const { t } = useTranslation(['reports']);

  const [reportType, setReportType] = useState<ReportType | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const validationError = (() => {
    if (!reportType) return t('reports:validation.typeRequired');
    if (!from) return t('reports:validation.fromRequired');
    if (!to) return t('reports:validation.toRequired');
    if (from > to) return t('reports:validation.fromAfterTo');
    if (getDaysDiff(from, to) > MAX_RANGE_DAYS) return t('reports:validation.rangeExceeds90Days');
    return null;
  })();

  const isFormValid = validationError === null;

  // P-12: isPending=true deshabilitará el botón en el template → no double-submit
  const { trigger, isPending, isError } = useExportCsv();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || isPending) return;
    trigger({ type: reportType as ReportType, from, to });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">{t('reports:page.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('reports:page.subtitle')}</p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>{t('reports:page.title')}</CardTitle>
          <CardDescription>{t('reports:page.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* Tipo de reporte */}
            <div className="space-y-1.5">
              <Label htmlFor="report-type">{t('reports:form.type.label')}</Label>
              <Select
                value={reportType}
                onValueChange={(value) => setReportType(value as ReportType)}
              >
                <SelectTrigger id="report-type" aria-label={t('reports:form.type.label')}>
                  <SelectValue placeholder={t('reports:form.type.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`reports:form.type.options.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Fecha desde */}
            <div className="space-y-1.5">
              <Label htmlFor="report-from">{t('reports:form.from.label')}</Label>
              <Input
                id="report-from"
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
                aria-label={t('reports:form.from.label')}
              />
            </div>

            {/* Fecha hasta */}
            <div className="space-y-1.5">
              <Label htmlFor="report-to">{t('reports:form.to.label')}</Label>
              <Input
                id="report-to"
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
                aria-label={t('reports:form.to.label')}
              />
            </div>

            {/* Validación inline (solo cuando hay valores y son inválidos) */}
            {!isFormValid && (from || to || reportType) && (
              <p className="text-sm text-destructive" role="alert">
                {validationError}
              </p>
            )}

            {/* Error de API */}
            {isError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t('reports:errors.title')}</AlertTitle>
                <AlertDescription>{t('reports:errors.generic')}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              disabled={!isFormValid || isPending}
              className="w-full"
            >
              <Download className="mr-2 h-4 w-4" />
              {isPending
                ? '…'
                : t('reports:form.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
